/**
 * S-curve report.
 * Route: /reports/s-curve
 * Loads GET /dpr/apis/planning/s-curve and plots the returned cumulative percents.
 * Does not recompute planned or actual progress from tasks.
 */
import { useEffect, useMemo, useState } from 'react';
import { Alert, Box, Button, Group, Paper, SegmentedControl, Skeleton, Stack, Text, Title } from '@mantine/core';
import {
  fetchSCurve,
  S_CURVE_GRANULARITIES,
  S_CURVE_METRICS,
  type SCurveData,
  type SCurveGranularity,
  type SCurveMetric,
} from '../../api/sCurveApi';
import { usePortalProject } from '../../contexts/PortalProjectContext';
import { parseDateSafe } from '../../utils/utils';
import SCurveChart from './components/SCurveChart';
import SCurvePointsTable from './components/SCurvePointsTable';
import { mapSCurveChart } from './logic/mapSCurveChart';

const METRIC_OPTIONS: { value: SCurveMetric; label: string }[] = [
  { value: 'QUANTITY', label: 'Quantity' },
  { value: 'SCHEDULE_PERCENT', label: 'Schedule %' },
];

const GRANULARITY_OPTIONS: { value: SCurveGranularity; label: string }[] = [
  { value: 'DAY', label: 'Day' },
  { value: 'WEEK', label: 'Week' },
  { value: 'MONTH', label: 'Month' },
  { value: 'QUARTER', label: 'Quarter' },
  { value: 'YEAR', label: 'Year' },
];

const controlStyles = {
  root: {
    border: '1px solid var(--mantine-color-default-border)',
  },
} as const;

const refreshButtonStyles = {
  root: {
    backgroundColor: 'var(--mantine-color-body)',
    color: 'var(--mantine-color-text)',
    border: '1px solid var(--mantine-color-default-border)',
    fontWeight: 500,
    '&:hover': {
      backgroundColor: 'var(--mantine-color-default-hover)',
    },
  },
} as const;

function formatWindowDate(value: string | null | undefined): string {
  const d = parseDateSafe(value ?? null);
  if (!d) return value || '—';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

export default function SCurvePage() {
  const { projectId, projectName } = usePortalProject();
  const projectLabel = projectName || projectId || 'this project';
  const [metric, setMetric] = useState<SCurveMetric>('QUANTITY');
  const [granularity, setGranularity] = useState<SCurveGranularity>('MONTH');
  const [data, setData] = useState<SCurveData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (!projectId) {
      setData(null);
      setError(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setData(null);
    setLoading(true);
    setError(null);
    fetchSCurve({ projectId, metric, granularity })
      .then((next) => {
        if (cancelled) return;
        setData(next);
      })
      .catch(() => {
        if (cancelled) return;
        setData(null);
        setError('Could not load the S-curve for this project.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [projectId, metric, granularity, reloadKey]);

  const model = useMemo(() => (data ? mapSCurveChart(data) : null), [data]);

  return (
    <Box
      style={{
        height: '100%',
        overflowY: 'auto',
        overflowX: 'hidden',
        backgroundColor: 'var(--mantine-color-body)',
      }}
    >
      <Box py="xl" px="md" style={{ width: '100%' }}>
        <Stack gap="lg">
          <Group justify="space-between" align="flex-start" wrap="wrap" gap="md">
            <Stack gap={4} style={{ minWidth: 0, flex: 1 }}>
              <Title order={2}>S-curve</Title>
              <Text c="dimmed" size="sm">
                Planned and actual cumulative progress for {projectLabel}.
              </Text>
            </Stack>
            <Button
              variant="default"
              onClick={() => setReloadKey((n) => n + 1)}
              loading={loading}
              disabled={!projectId}
              styles={refreshButtonStyles}
            >
              Refresh
            </Button>
          </Group>

          <Group gap="md" align="flex-end" wrap="wrap">
            <Stack gap={4}>
              <Text size="xs" c="dimmed">
                Metric
              </Text>
              <SegmentedControl
                value={metric}
                onChange={(value) => {
                  if ((S_CURVE_METRICS as readonly string[]).includes(value)) {
                    setMetric(value as SCurveMetric);
                  }
                }}
                data={METRIC_OPTIONS}
                styles={controlStyles}
              />
            </Stack>
            <Stack gap={4}>
              <Text size="xs" c="dimmed">
                Granularity
              </Text>
              <SegmentedControl
                value={granularity}
                onChange={(value) => {
                  if ((S_CURVE_GRANULARITIES as readonly string[]).includes(value)) {
                    setGranularity(value as SCurveGranularity);
                  }
                }}
                data={GRANULARITY_OPTIONS}
                styles={controlStyles}
              />
            </Stack>
          </Group>

          {!projectId ? (
            <Text c="dimmed">Select a project to load the S-curve.</Text>
          ) : null}

          {error ? (
            <Alert color="red" variant="light">
              {error}
            </Alert>
          ) : null}

          {model?.estimateNote ? (
            <Alert color="yellow" variant="light">
              {model.estimateNote}
            </Alert>
          ) : null}

          {loading && !model ? <Skeleton h={360} radius="sm" /> : null}

          {model && model.rows.length === 0 ? (
            <Text c="dimmed">No S-curve points for this project and metric.</Text>
          ) : null}

          {model && model.rows.length > 0 && data ? (
            <>
              <Text size="xs" c="dimmed">
                {formatWindowDate(data.from)} – {formatWindowDate(data.to)}. Actual is reported through{' '}
                {formatWindowDate(data.asOf)}.
              </Text>
              <Paper withBorder radius="sm" p="md" bg="var(--mantine-color-body)">
                <SCurveChart rows={model.rows} showPeriodAmounts={model.showPeriodAmounts} />
              </Paper>
              <Paper withBorder radius="sm" p={0} bg="var(--mantine-color-body)" style={{ overflow: 'hidden' }}>
                <SCurvePointsTable
                  rows={model.rows}
                  showPeriodAmounts={model.showPeriodAmounts}
                  unit={data.unit}
                />
              </Paper>
            </>
          ) : null}
        </Stack>
      </Box>
    </Box>
  );
}
